/**
 * #68 (USER ORDER 2026-08-09): the UI interaction stream.
 *
 * A document-level listener captures clicks (with a compact structural label
 * of what was clicked), page visibility changes and copy events, batches them
 * per active conversation, and flushes to POST /v1/telemetry/ui-events —
 * where the backend buffers them and embeds them into the combined-debug
 * export as `uiEvents`. Before this the export had no click stream at all.
 *
 * Fire-and-forget: telemetry must never affect the app. Flushes every 5s
 * when events are pending, and immediately on pagehide/visibilitychange via
 * sendBeacon so navigation away doesn't lose the tail.
 */

import { fetchWithAuth } from '../api/fetchWithAuth';
import { marchApiUrlRaw } from '../api/marchApiEnv';

type UiEvent = {
  type: string;
  target?: string;
  label?: string;
  page?: string;
  timestamp: string;
};

let activeConversationId = '';
let pending: UiEvent[] = [];
let flushTimer: ReturnType<typeof setInterval> | null = null;
let installed = false;

const MAX_PENDING = 500;
const FLUSH_INTERVAL_MS = 5000;

export function setUiTelemetryConversation(conversationId: string): void {
  const next = (conversationId || '').trim();
  if (next && next !== activeConversationId && pending.length > 0) {
    // Conversation switched with unsent events: flush them under the id they
    // belong to before rebinding.
    void flushUiEvents();
  }
  activeConversationId = next;
}

function describeTarget(element: EventTarget | null): { target: string; label: string } {
  if (!(element instanceof Element)) return { target: '', label: '' };
  const interactive = element.closest(
    'button, a, [role="button"], [role="tab"], summary, input, select, textarea'
  );
  const node = interactive ?? element;
  const tag = node.tagName.toLowerCase();
  const id = node.id ? `#${node.id}` : '';
  const testId = node.getAttribute('data-testid');
  const aria = node.getAttribute('aria-label');
  const text = (node.textContent || '').trim().replace(/\s+/g, ' ').slice(0, 80);
  return {
    target: `${tag}${id}${testId ? `[data-testid=${testId}]` : ''}`,
    label: aria || text,
  };
}

function push(event: UiEvent): void {
  pending.push(event);
  if (pending.length > MAX_PENDING) pending = pending.slice(-MAX_PENDING);
}

export async function flushUiEvents(useBeacon = false): Promise<void> {
  if (!activeConversationId || pending.length === 0) return;
  const events = pending;
  pending = [];
  const body = JSON.stringify({ conversation_id: activeConversationId, events });
  const url = `${marchApiUrlRaw()}/v1/telemetry/ui-events`;
  try {
    if (useBeacon && typeof navigator !== 'undefined' && navigator.sendBeacon) {
      // Beacon carries no auth header; acceptable only as the last-gasp path —
      // the server treats an unauthenticated beacon like any other rejected
      // request and the events are simply lost, never an error surfaced here.
      navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
      return;
    }
    await fetchWithAuth(url, {
      method: 'POST',
      body,
      extraHeaders: { 'Content-Type': 'application/json' },
    });
  } catch {
    // Telemetry never surfaces errors; drop rather than retry-loop.
  }
}

export function installUiEventTelemetry(): void {
  if (installed || typeof document === 'undefined') return;
  installed = true;

  document.addEventListener(
    'click',
    (event) => {
      const { target, label } = describeTarget(event.target);
      push({
        type: 'click',
        target,
        label,
        page: typeof window !== 'undefined' ? window.location.pathname : '',
        timestamp: new Date().toISOString(),
      });
    },
    { capture: true, passive: true }
  );

  document.addEventListener('visibilitychange', () => {
    push({
      type: document.visibilityState === 'hidden' ? 'page_hidden' : 'page_visible',
      page: typeof window !== 'undefined' ? window.location.pathname : '',
      timestamp: new Date().toISOString(),
    });
    if (document.visibilityState === 'hidden') void flushUiEvents(true);
  });

  if (typeof window !== 'undefined') {
    window.addEventListener('pagehide', () => void flushUiEvents(true));
  }

  flushTimer = setInterval(() => void flushUiEvents(), FLUSH_INTERVAL_MS);
  void flushTimer;
}
